// src/components/Quotes.jsx
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { jsPDF } from "jspdf";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EmailHelper from "./helpers/EmailHelper";
import { canAccess } from "../hooks/usePermission";
import "./Quotes.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.magnirauniverse.com";

const filterQuoteOption = (option, inputValue) => {
  const search = String(inputValue || "")
    .toLowerCase()
    .trim();

  if (!search) return true;

  const item = option.data || {};

  const searchableText = `
    ${item.label || ""}
    ${item.value || ""}
    ${item.description || ""}
    ${item.category || ""}
    ${item.room || ""}
    ${item.MRP || ""}
    ${item.length || ""}
    ${item.width || ""}
  `.toLowerCase();

  return searchableText.includes(search);
};
const Quotes = ({ project }) => {
  const navigate = useNavigate();
  const [selectedUnit, setSelectedUnit] = useState("NOS");
  const [selectedQty, setSelectedQty] = useState(1);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemSearchText, setItemSearchText] = useState("");
  const [quoteMasterOptions, setQuoteMasterOptions] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [pmFeePercentage, setPmFeePercentage] = useState(0);
  const [applyPmFee, setApplyPmFee] = useState(false);
  const [gstPercentage, setGstPercentage] = useState(18);
  const [applyGst, setApplyGst] = useState(false);
  const [gstMode, setGstMode] = useState("exclusive");
  const [quoteDocuments, setQuoteDocuments] = useState([]);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [showCustomRow, setShowCustomRow] = useState(false);
  const [customItem, setCustomItem] = useState({
    image: "",
    label: "",
    description: "",
    room: "",
    unit: "NOS",
    quantity: 1,
    length: "",
    width: "",
    MRP: "",
  });
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [approvedQuoteIndex, setApprovedQuoteIndex] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [quoteSaved, setQuoteSaved] = useState(false);
  const [masterItemsOptions, setMasterItemsOptions] = useState([]);
  const [clientDetails, setClientDetails] = useState({});
  const [editId, setEditId] = useState(null);
  const canAddQuoteItem = canAccess("quote", "quoteAddItem");
  const canCreateCustomerQuote = canAccess("quote", "quoteCreateCustomer");
  const canAddMasterItem = canAccess("quote", "quoteAddCustomItem");
  const canSaveQuote = canAccess("quote", "quoteSave");
  const canEditSavedQuote = canAccess("quote", "quoteEditSaved");
  const canApproveRejectQuote = canAccess("quote", "quoteApproveReject");
  const canExportPdf = canAccess("quote", "quoteExportPdf");
  const canExportExcel = canAccess("quote", "quoteExportExcel");
  const canSendMail = canAccess("quote", "quoteSendMail");
  const canShowFinalAmount = canAccess("quote", "quoteShowFinalAmount");
  const canKitchen = canAccess("quote", "kitchen");
  const canLooseFurniture = canAccess("quote", "looseFurniture");
  const canSiteService = canAccess("quote", "siteService");
  const canGodrejInterio = canAccess("quote", "godrejInterio");
  const canModularFurniture = canAccess("quote", "modularFurniture");
  const loggedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = loggedUser?.role === "admin";
  const canShowPrice = isAdmin || canAccess("quote", "quoteShowPrice");
  const roomOptions = [
    { label: "Living Room", value: "Living Room" },
    { label: "Kitchen", value: "Kitchen" },
    { label: "Dining", value: "Dining" },
    { label: "Pooja Room", value: "Pooja Room" },
    { label: "Master Bedroom", value: "Master Bedroom" },
    { label: "Bedroom-1", value: "Bedroom-1" },
    { label: "Bedroom-2", value: "Bedroom-2" },
    { label: "Bedroom-3", value: "Bedroom-3" },
    { label: "Bedroom-4", value: "Bedroom-4" },
    { label: "Bedroom-5", value: "Bedroom-5" },
    { label: "Kids Bedroom", value: "Kids Bedroom" },
    { label: "Parents Bedroom", value: "Parents Bedroom" },
    { label: "Guest Bedroom", value: "Guest Bedroom" },
    { label: "Storage", value: "Storage" },
    { label: "Server Room", value: "Server Room" },
    { label: "Wash Room", value: "Wash Room" },
    { label: "Pantry", value: "Pantry" },
    { label: "Utility", value: "Utility" },
    { label: "Overall Flat", value: "Overall Flat" },
    { label: "Foyer Area", value: "Foyer Area" },
    { label: "Vestibule", value: "Vestibule" },
    { label: "Balcony", value: "Balcony" },
  ];

  const refetchQuotes = async (pName) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/quotes?projectName=${encodeURIComponent(pName)}`,
      );
      setSavedQuotes(res.data);
    } catch (err) {
      if (err.response?.status === 404) setSavedQuotes([]);
      else console.error("Error fetching quotes:", err);
    }
  };
  // inside useEffect
  useEffect(() => {
    const loadQuoteMaster = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/quote-master`);

        const toNum = (v) =>
          Number(
            String(v || "0")
              .replace(/[₹,%\s]/g, "")
              .replace(/,/g, ""),
          ) || 0;

        const data = res.data.map((item) => ({
          ...item,
          MRP: toNum(
            item.MRP ??
              item.mrp ??
              item.price ??
              item.Rate ??
              item.rate ??
              item["M.R.P"] ??
              item["M R P"] ??
              item["Maximum Retail Price"] ??
              item["MaximumRetailPrice"],
          ),
          price: toNum(
            item.MRP ??
              item.mrp ??
              item.price ??
              item.Rate ??
              item.rate ??
              item["M.R.P"] ??
              item["M R P"] ??
              item["Maximum Retail Price"] ??
              item["MaximumRetailPrice"],
          ),
        }));

        console.log("DIRECT API FIRST ITEM:", data[0]);
        console.log("RAW API FIRST ITEM:", res.data[0]);
        console.log("ALL KEYS:", Object.keys(res.data[0] || {}));
        const normalizeCategory = (value = "") =>
          String(value).trim().toLowerCase().replace(/\s+/g, "");

        const filteredData = data.filter((item) => {
          if (isAdmin) return true;

          const category = normalizeCategory(item.category);

          if (category === "kitchen" && canKitchen) return true;
          if (category === "loosefurniture" && canLooseFurniture) return true;
          if (category === "siteservice" && canSiteService) return true;
          if (category === "godrejinterio" && canGodrejInterio) return true;
          if (category === "modularfurniture" && canModularFurniture)
            return true;

          return false;
        });
        const uniqueByLabel = Object.values(
          filteredData.reduce((acc, item) => {
            const key = String(item.label || "").trim();

            const currentMRP = Number(item.MRP || item.price || 0);
            const oldMRP = Number(acc[key]?.MRP || acc[key]?.price || 0);

            if (!acc[key] || currentMRP > oldMRP) {
              acc[key] = item;
            }

            return acc;
          }, {}),
        );
        const formattedItems = uniqueByLabel.map((item) => ({
          itemId: item._id,
          label: item.label,
          value: item._id,
          description: item.description || "",
          image: item.image || "",
          MRP: Number(item.MRP ?? item.price ?? 0),
          price: Number(item.price ?? item.MRP ?? 0),
          length: Number(item.length) || 0,
          width: Number(item.width) || 0,
          category: item.category || "",
        }));

        setQuoteMasterOptions(formattedItems); // ✅ correct
      } catch (err) {
        console.error("QuoteMaster load failed", err);
      }
    };

    loadQuoteMaster();
  }, [
    canKitchen,
    canLooseFurniture,
    canSiteService,
    canGodrejInterio,
    canModularFurniture,
  ]);
  useEffect(() => {
    const fetchMasterItems = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/master-items`);
        if (response.data) {
          const formattedItems = response.data.map((item) => ({
            itemId: item.itemId || item._id,
            label: item.label,
            value: item.value,
            description: item.description,
            image: item.image,
            room: item.room,
            MRP: item.MRP,
            length: item.length,
            width: item.width,
          }));
          setMasterItemsOptions(formattedItems);
        }
      } catch (error) {
        console.error("Error fetching master items:", error);
      }
    };

    if (project) {
      setItems([]);
      setSavedQuotes([]);
      setSelectedItem(null);
      setSelectedRoom(null);
      setDiscountPercentage(0);
      setApplyDiscount(false);
      setPmFeePercentage(0);
      setApplyPmFee(false);
      setGstPercentage(18);
      setApplyGst(false);
      setGstMode("exclusive");
      setQuoteDocuments([]);
      setShowCustomRow(false);
      setIsEditing(false);
      setEditIndex(null);
      setEditId(null);

      const savedClientDetails =
        JSON.parse(localStorage.getItem(`clientDetails-${project.name}`)) || {};
      setClientName(savedClientDetails.ClientName || "");
      setClientAddress(savedClientDetails.clientAddress || "");

      const savedApprovedIndex =
        JSON.parse(
          localStorage.getItem(`approvedQuoteIndex-${project.name}`),
        ) || null;
      setApprovedQuoteIndex(savedApprovedIndex);

      const savedStatus = localStorage.getItem(`quoteSaved-${project.name}`);
      setQuoteSaved(savedStatus === "true");

      refetchQuotes(project.name);

      axios
        .get(
          `${BASE_URL}/api/client-details/${encodeURIComponent(project.name)}`,
        )
        .then((res) => {
          const details = res.data?.clientDetails || {};

          setClientName(details.ClientName || "");
          setClientAddress(details.clientAddress || "");
          setClientDetails(details);
        })
        .catch(() => setClientDetails({}));
    }

    fetchMasterItems();
  }, [project]);

  // Approve/Reject (status only)
  const handleStatusChange = async (index) => {
    const quote = savedQuotes[index];
    const newStatus = quote.status === "Approved " ? "Rejected" : "Approved";
    try {
      await axios.put(`${BASE_URL}/api/quotes/update/${quote._id}`, {
        status: newStatus,
      });
      const updatedQuotes = savedQuotes.map((q, i) =>
        i === index
          ? { ...q, status: newStatus }
          : { ...q, status: "Rejected" },
      );
      setSavedQuotes(updatedQuotes);
    } catch (error) {
      console.error(
        "❌ Failed to update status:",
        error.response?.data || error,
      );
      alert("❌ Status update failed.");
    }
  };

  // CREATE (always) — even in edit mode we create a NEW version with today's date
  const saveQuote = async () => {
    if (!clientName || items.length === 0) {
      alert("Please enter a client name and add at least one item.");
      return;
    }

    const {
      totalMRP,
      projectManagementFee,
      discountAmount,
      gstAmount,
      finalValue,
    } = calculateTotal();

    // Always use today's date for new row
    const quoteDate = new Date().toISOString().split("T")[0];

    const payload = {
      clientName,
      clientAddress,
      items,
      totalMRP,
      projectManagementFee,
      projectManagementFeePercentage: applyPmFee
        ? Number(Number(pmFeePercentage || 0).toFixed(2))
        : 0,
      discountAmount,
      discountPercentage: applyDiscount ? Number(discountPercentage || 0) : 0,
      gstAmount,
      gstPercentage: applyGst ? Number(gstPercentage || 0) : 0,
      gstMode: applyGst ? gstMode : "exclusive",
      inclusiveGST: applyGst && gstMode === "inclusive",
      taxableValue: finalValue - gstAmount,
      applyDiscount,
      applyProjectManagementFee: applyPmFee,
      applyGST: applyGst,
      finalAmount: finalValue,
      date: quoteDate,

      projectName: project.name,
      projectId: project._id || project.id, // ✅ Add

      documents: quoteDocuments,
      clientDetails: {
        ...(clientDetails || {}),
        quoteDocuments,
      },

      status: "Rejected",
      custom: false,
    };

    try {
      // 🔁 IMPORTANT: Never send _id here — we want a fresh quote row
      await axios.post(`${BASE_URL}/api/quotes/save`, payload);

      await refetchQuotes(project.name);
      setQuoteSaved(true);
      localStorage.setItem(`quoteSaved-${project.name}`, "true");

      // reset edit state
      setItems([]);
      setSelectedItem(null);
      setSelectedRoom(null);
      setDiscountPercentage(0);
      setApplyDiscount(false);
      setPmFeePercentage(0);
      setApplyPmFee(false);
      setGstPercentage(18);
      setApplyGst(false);
      setGstMode("exclusive");
      setQuoteDocuments([]);
      setShowCustomRow(false);
      setIsEditing(false);
      setEditIndex(null);
      setEditId(null);

      alert("Quote saved as a new version!");
    } catch (err) {
      console.error("❌ Save error:", err);
      alert(
        err?.response?.data?.message ||
          "Failed to save quote. Please try again.",
      );
    }
  };

  // EDIT: custom → builder; non-custom → bring into form (but save creates new row)
  const handleEditQuote = (index) => {
    const quote = savedQuotes[index];

    // Detect custom quotes robustly
    const isCustom =
      !!quote.custom ||
      quote.title === "Custom Quote" ||
      typeof quote.globalDiscountPct === "number" ||
      (Array.isArray(quote.items) &&
        quote.items.some(
          (it) => it?.discountPct != null || it?.netAfterRow != null,
        ));

    if (isCustom) {
      navigate("/customer-quote", {
        state: quote._id ? { quoteId: quote._id } : { quote },
      });
      return;
    }

    // Non-custom: load into form for edits (we'll save as new row)
    const processedItems = Array.isArray(quote.items)
      ? quote.items.map((item) => ({
          ...item,
          label: item.label || item.name || "",
          room: item.room || "",
          unit: item.unit || "NOS",
          quantity: item.quantity ?? 1,
          length: item.length || "",
          width: item.width || "",
          area:
            item.area ||
            (item.length && item.width ? item.length * item.width : 0),
          MRP: item.MRP || item.price || 0,
        }))
      : [];

    setItems(processedItems);
    const savedDiscountPct = Number(
      quote.discountPercentage ??
        (quote.totalMRP
          ? (Number(quote.discountAmount || 0) / Number(quote.totalMRP || 1)) *
            100
          : 0),
    );
    const afterDiscount =
      Number(quote.totalMRP || 0) - Number(quote.discountAmount || 0);
    const articleValue = Number(quote.totalMRP || 0);
    const savedPmPct = Number(
      quote.projectManagementFeePercentage ??
        (articleValue > 0
          ? (Number(quote.projectManagementFee || 0) / articleValue) * 100
          : 0),
    );
    const savedGstMode =
      quote.gstMode === "inclusive" || quote.inclusiveGST === true
        ? "inclusive"
        : "exclusive";
    const gstBase =
      savedGstMode === "inclusive"
        ? Number(quote.finalAmount || 0) - Number(quote.gstAmount || 0)
        : afterDiscount + Number(quote.projectManagementFee || 0);
    const savedGstPct = Number(
      quote.gstPercentage ??
        (gstBase > 0 ? (Number(quote.gstAmount || 0) / gstBase) * 100 : 0),
    );

    setDiscountPercentage(savedDiscountPct);
    setApplyDiscount(savedDiscountPct > 0 || Boolean(quote.applyDiscount));
    setPmFeePercentage(Number(savedPmPct.toFixed(2)));
    setApplyPmFee(savedPmPct > 0 || Boolean(quote.applyProjectManagementFee));
    setGstPercentage(savedGstPct > 0 ? Number(savedGstPct.toFixed(2)) : 18);
    setApplyGst(savedGstPct > 0 || Boolean(quote.applyGST));
    setGstMode(savedGstMode);
    setQuoteDocuments(
      Array.isArray(quote.documents)
        ? quote.documents
        : Array.isArray(quote.clientDetails?.quoteDocuments)
          ? quote.clientDetails.quoteDocuments
          : [],
    );

    setIsEditing(true);
    setEditIndex(index);
    setEditId(quote._id); // only for UI
    setQuoteSaved(false);
    localStorage.removeItem(`quoteSaved-${project.name}`);
  };
  const handleCancelEdit = () => {
    setItems([]);
    setSelectedItem(null);
    setSelectedRoom(null);

    setDiscountPercentage(0);
    setApplyDiscount(false);

    setPmFeePercentage(0);
    setApplyPmFee(false);

    setGstPercentage(18);
    setApplyGst(false);
    setGstMode("exclusive");

    setQuoteDocuments([]);
    setShowCustomRow(false);

    setIsEditing(false);
    setEditIndex(null);
    setEditId(null);

    setQuoteSaved(true);
  };
  const allQuoteOptions = quoteMasterOptions;

  const filteredQuoteOptions = allQuoteOptions.filter((item) => {
    const search = itemSearchText.toLowerCase().trim();

    if (!search) return true;

    const searchableText = `
    ${item.label || ""}
    ${item.value || ""}
    ${item.description || ""}
    ${item.category || ""}
    ${item.room || ""}
    ${item.MRP || ""}
    ${item.length || ""}
    ${item.width || ""}
  `.toLowerCase();

    return searchableText.includes(search);
  });
  const highlightMatch = (text, search) => {
    if (!search) return text;

    const regex = new RegExp(`(${search})`, "gi");

    return String(text).replace(
      regex,
      `<span style="background:#fef08a;color:#000;font-weight:700;padding:1px 2px;border-radius:3px;">$1</span>`,
    );
  };
  const addItem = () => {
    if (!selectedItem || !selectedRoom) {
      alert("Please select both item and room");
      return;
    }

    const qty = Number(selectedQty);

    if (!Number.isFinite(qty) || qty <= 0) {
      alert("Please enter a valid quantity.");
      return;
    }

    const originalItem =
      quoteMasterOptions.find((it) => it.itemId === selectedItem.itemId) ||
      quoteMasterOptions.find((it) => it.value === selectedItem.value) ||
      quoteMasterOptions.find((it) => it.label === selectedItem.label) ||
      selectedItem;

    const finalMRP = Number(
      originalItem.MRP ??
        originalItem.price ??
        selectedItem.MRP ??
        selectedItem.price ??
        0,
    );

    const itemWithRoom = {
      ...originalItem,
      room: selectedRoom.value,
      unit: selectedUnit,
      quantity: qty,
      rate: finalMRP,
      MRP: finalMRP * qty,
      price: finalMRP * qty,
      length: Number(originalItem.length || 0),
      width: Number(originalItem.width || 0),
      area: Number(originalItem.length || 0) * Number(originalItem.width || 0),
      itemId: originalItem.itemId || originalItem.value || Date.now(),
    };

    setItems((prev) => [...prev, itemWithRoom]);
    setSelectedItem(null);
    setSelectedRoom(null);
    setSelectedUnit("NOS");
    setSelectedQty(1);
  };
  const resetCustomItem = () => {
    setCustomItem({
      image: "",
      label: "",
      description: "",
      room: "",
      length: "",
      width: "",
      MRP: "",
    });
  };

  const handleCustomImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomItem((prev) => ({ ...prev, image: reader.result || "" }));
    };
    reader.readAsDataURL(file);
  };

  const addCustomItemInline = () => {
    if (!String(customItem.label || "").trim()) {
      alert("Please enter custom item name.");
      return;
    }
    if (!customItem.room) {
      alert("Please select room for custom item.");
      return;
    }

    const length = Number(customItem.length || 0);
    const width = Number(customItem.width || 0);
    const mrp = Number(customItem.MRP || 0);
    const customQty = Number(customItem.quantity);
    if (!Number.isFinite(customQty) || customQty <= 0) {
      alert("Please enter a valid quantity.");
      return;
    }

    const itemToAdd = {
      ...customItem,
      label: String(customItem.label).trim(),
      description: String(customItem.description || "").trim(),
      unit: customItem.unit || "NOS",
      quantity: customQty,
      rate: mrp,
      length,
      width,
      area: length * width,
      MRP: mrp * customQty,
      price: mrp * customQty,
      itemId: `custom-${Date.now()}`,
      isCustomItem: true,
    };

    setItems((prev) => [...prev, itemToAdd]);
    resetCustomItem();
    setShowCustomRow(false);
  };

  const updateCustomField = (field, value) => {
    setCustomItem((prev) => ({ ...prev, [field]: value }));
  };

  const getQuoteFileUrl = (url = "") => {
    const value = String(url || "").trim();
    if (!value) return "";
    if (value.startsWith("http://") || value.startsWith("https://"))
      return value;
    return `${BASE_URL}${value.startsWith("/") ? value : `/${value}`}`;
  };

  const handleQuoteDocumentUpload = async (file) => {
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/jpeg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Please upload PDF, Word, Excel, JPG or PNG document only.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("Document size cannot exceed 10 MB.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setDocumentUploading(true);
      const res = await axios.post(`${BASE_URL}/api/quotes/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedUrl =
        res.data?.url ||
        res.data?.fileUrl ||
        res.data?.path ||
        res.data?.file ||
        res.data?.data?.url ||
        res.data?.data?.fileUrl;

      if (!uploadedUrl) {
        console.error("Document upload response:", res.data);
        alert("Document uploaded but file URL was not returned by the server.");
        return;
      }

      setQuoteDocuments((prev) => [
        ...prev,
        {
          name: file.name,
          url: uploadedUrl,
          type: file.type,
          size: file.size,
        },
      ]);
    } catch (err) {
      console.error("Document upload failed:", err.response?.data || err);
      alert(err.response?.data?.message || "Document upload failed.");
    } finally {
      setDocumentUploading(false);
    }
  };

  const removeQuoteDocument = (index) => {
    setQuoteDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    const round2 = (value) =>
      Math.round((Number(value) + Number.EPSILON) * 100) / 100;

    const totalMRP = round2(
      items.reduce((sum, item) => sum + Number(item.MRP ?? item.price ?? 0), 0),
    );

    const discountPct = applyDiscount ? Number(discountPercentage || 0) : 0;

    const pmPct = applyPmFee ? Number(pmFeePercentage || 0) : 0;

    const gstPct = applyGst ? Number(gstPercentage || 0) : 0;

    // STEP 1: PM fee on original article value
    const projectManagementFee = round2((totalMRP * pmPct) / 100);

    // STEP 2: Article value + PM fee
    const subtotal = round2(totalMRP + projectManagementFee);

    // STEP 3: Discount on Article Value only (PM fee excluded)
    const discountAmount = round2((totalMRP * discountPct) / 100);

    // STEP 4: Taxable amount
    const afterDiscount = round2(subtotal - discountAmount);

    const beforeGst = afterDiscount;

    // STEP 5: GST
    const gstAmount = !applyGst
      ? 0
      : gstMode === "inclusive"
        ? round2((beforeGst * gstPct) / (100 + gstPct))
        : round2((beforeGst * gstPct) / 100);

    // STEP 6: Final amount
    const finalValue = round2(
      applyGst && gstMode === "exclusive" ? beforeGst + gstAmount : beforeGst,
    );

    const taxableValue = round2(finalValue - gstAmount);

    return {
      totalMRP,
      projectManagementFee,
      subtotal,
      discountAmount,
      afterDiscount,
      beforeGst,
      gstAmount,
      finalValue,
      taxableValue,
      discountPct,
      pmPct,
      gstPct,
      gstMode,
    };
  };

  // ======== EXPORTS ========
  // PDF Export

  const exportToPDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;

    const roomSummaryData = Object.values(
      items.reduce((acc, item) => {
        if (item.room) {
          const discountedPrice = applyDiscount
            ? item.MRP - (item.MRP * discountPercentage) / 100
            : item.MRP;
          if (!acc[item.room]) {
            acc[item.room] = { roomName: item.room, cost: 0 };
          }
          acc[item.room].cost += discountedPrice || 0;
        }
        return acc;
      }, {}),
    );

    const drawFooter = () => {
      const footerHeight = 12;
      const footerY = pdfHeight - footerHeight;
      pdf.setFillColor(52, 58, 118);
      pdf.rect(0, footerY, pdfWidth, footerHeight, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.text(
        "SF 9 Time Square 2, Nr. Sindhu Bhavan Road, Thaltej, Ahmedabad-380059 | Mobile: 9998038595",
        pdfWidth / 2,
        pdfHeight - 4,
        { align: "center" },
      );
    };
    const loginUserName =
      JSON.parse(localStorage.getItem("user"))?.name || "Team SVB";

    const renderToPDFPages = async (div) => {
      const canvas = await html2canvas(div, {
        scale: 2,
        backgroundColor: "#d3d3d3",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      const PAGE_EPSILON = 1; // prevents a tiny rounding overflow creating a blank page

      const drawGreyPage = () => {
        pdf.setFillColor(211, 211, 211);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
      };

      drawGreyPage();

      // A4-sized sections can become a fraction taller after html2canvas rounding.
      // Fit that tiny overflow on the current page instead of creating a blank page.
      if (imgHeight <= pdfHeight + PAGE_EPSILON) {
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          0,
          pdfWidth,
          Math.min(imgHeight, pdfHeight),
        );
        drawFooter();
        return;
      }

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
      drawFooter();
      heightLeft -= pdfHeight;

      while (heightLeft > PAGE_EPSILON) {
        position -= pdfHeight;
        pdf.addPage();
        drawGreyPage();
        pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
        drawFooter();
        heightLeft -= pdfHeight;
      }
    };

    // ✅ 1. COVER PAGE FIRST
    const coverDiv = document.createElement("div");
    coverDiv.style.width = "210mm";
    coverDiv.style.height = "297mm";
    coverDiv.style.padding = "0"; // Top-aligned
    coverDiv.style.boxSizing = "border-box";
    coverDiv.style.fontFamily = "Calibri, sans-serif";
    coverDiv.style.backgroundColor = "d3d3d3";
    coverDiv.style.margin = "0";
    coverDiv.style.position = "relative"; // important for absolute bottom image

    coverDiv.innerHTML = `
  <!-- Sticky Header Section -->
 <div style="width: 100%; display: flex; justify-content: space-between; align-items: center; padding: 15px 25px 10px 25px;">
  
  <!-- Left Side - Company Name -->
  <div style="font-size: 26px; font-weight: bold; color: #343A76;">
    SOFTVOLUTE BUILDCON LLP
  </div>

  <!-- Right Side - Logo with left margin -->
  <div style="margin-right: 40px;">   <!-- 🔥 Move logo slightly left -->
    <img src="/logo.jpeg" alt="logo" style="width: 60px; height: auto;" />
  </div>

</div>


</div>


  <!-- Underline -->
  <div style="width: 100%; border-bottom: 2px solid  #343A76; margin: 0 25px 30px 25px;"></div>

  <!-- Title Section -->
  <h1 style="text-align: center; color:  #343A76; margin-top: 0px; font-size: 36px;">Site Service Quotation</h1>

  <!-- Subtitle -->
  <div style="text-align: center; margin-top: 15px;">
    <div style="display: inline-block; padding: 6px 12px; background: white; color:  #343A76; font-size: 16px; font-weight: 500; box-shadow: 0 5px 5px rgba(0,0,0,0.1);">
      Keep Your Costs On Track For Your New Home Interiors
    </div>
  </div>

  <!-- Paragraph Section -->
  <div style="margin: 50px 25px 180px 25px; font-size: 16px; color:  #343A76; line-height: 1.7;">
    Dear <strong>${clientName}</strong> & Family!<br/><br/>
    Please go through our estimated price quote for your project.<br/>
    It may upgrade as per your design elevates.<br/><br/>
    Tracking of budget & time can be crucial for this kind of works. But with SVB's transparent and informed systems, client stay aware of project updates.<br/><br/>
    Humans will be working at site so their craftmanship may take bit extra hours but quality assurance shall be confirmed from our side.<br/><br/>
    A Big Thank You for choosing SVB for your project & Welcome.<br/><br/>
    Let’s dive in!
  </div>

 <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px;">
  <!-- Left side text -->
  <div style="
  font-family: 'Lucida Calligraphy', cursive;
  font-size: 26px;
  color: #343A76;
  font-weight: normal;
  max-width: 50%;
  padding-left: 40px;
">
  Home is the place where every corner tells your story ....
</div>


  <!-- Right side image -->
  <div style="padding-right: 30px;">
    <img src="/relaxing img-02.jpg" style="width: 280px; max-height: 180px;" />
  </div>
</div>

`;

    // ✅ QUOTE TABLE SECTION - MULTIPLE PAGES (10 ITEMS PER PAGE)
    const quoteDivs = [];
    const itemsPerPage = 10;

    for (let i = 0; i < items.length; i += itemsPerPage) {
      const currentItems = items.slice(i, i + itemsPerPage);
      const quoteDiv = document.createElement("div");

      quoteDiv.style.width = "210mm";
      quoteDiv.style.minHeight = "297mm";
      quoteDiv.style.padding = `${margin}mm`;
      quoteDiv.style.boxSizing = "border-box";
      quoteDiv.style.fontFamily = "Calibri, sans-serif";
      quoteDiv.style.backgroundColor = "#d3d3d3";

      const showDiscount = applyDiscount && discountPercentage > 0;

      quoteDiv.innerHTML = `
    <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
    <div style="font-size: 14px;">
      Client: ${clientName}<br/>Address: ${clientAddress}
      <span style="float:right;">Date: ${new Date().toLocaleDateString(
        "en-IN",
      )}</span>
    </div>
    <h2 style="text-align:center; font-size: 20px; margin-top: 10px;">Quote Details</h2>

    <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 10px; background-color: #FFFFED; border: 0.5px solid black; border-radius: 8px; overflow: hidden;">
      <thead>
        <tr style="background-color: #707171; color: #FFFFED;">
          <th style="border: 0.5px solid lightgrey; padding: 8px;">Sr No</th>
          <th style="border: 0.5px solid lightgrey; padding: 8px;">Image</th>
          <th style="border: 0.5px solid lightgrey; padding: 8px;">Item Name</th>
          <th style="border: 0.5px solid lightgrey; padding: 8px;">Room</th>
          <th style="border: 0.5px solid lightgrey; padding: 8px; white-space: nowrap;">Price<br/>(INR)</th>
          ${
            showDiscount
              ? `<th style="border: 0.5px solid lightgrey; padding: 8px; white-space: nowrap;">Discounted Price</th>`
              : ""
          }
        </tr>
      </thead>
      <tbody>
        ${currentItems
          .map((item, index) => {
            const discounted = Math.round(
              item.MRP - (item.MRP * discountPercentage) / 100,
            );
            const priceCell = showDiscount
              ? `<span style="text-decoration: line-through; color: black; font-size: 15px; white-space: nowrap;">₹ ${item.MRP.toLocaleString(
                  "en-IN",
                )}</span>`
              : `<span style="color: black; font-size: 15px; white-space: nowrap;">₹ ${item.MRP.toLocaleString(
                  "en-IN",
                )}</span>`;

            return `
              <tr>
                <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">${
                  i + index + 1
                }</td>
                <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">
                  ${
                    item.image
                      ? `<img src="${item.image}" style="width: 80px; height: 50px; margin: 2px;" />`
                      : ""
                  }
                </td>
                <td style="border: 0.5px solid lightgrey; padding: 8px;"><strong>${
                  item.label
                }</strong><br/>${item.description || ""}</td>
                <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">${
                  item.room
                }</td>
                <td style="border: 0.5px solid lightgrey; text-align: right; padding: 8px; white-space: nowrap;">${priceCell}</td>
                ${
                  showDiscount
                    ? `<td style="border: 0.5px solid lightgrey; text-align: right; padding: 8px; color: green; font-weight: bold; white-space: nowrap;">₹ ${discounted.toLocaleString(
                        "en-IN",
                      )}</td>`
                    : ""
                }
              </tr>
            `;
          })
          .join("")}
      </tbody>
    </table>
  `;

      quoteDivs.push(quoteDiv);
    }

    // ✅ COST SUMMARY SECTION
    const showDiscount =
      applyDiscount && discountAmount > 0 && discountPercentage > 0
        ? `
    <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
      <span style="color: green; font-weight: bold;">Total Discount on Article Value (${discountPercentage}%):</span>
      <span style="color: green; font-weight: bold;">-₹ ${Math.round(
        discountAmount,
      ).toLocaleString("en-IN")}</span>
    </div>
    <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
  `
        : "";

    const showPmFee =
      applyPmFee && projectManagementFee > 0
        ? `
    <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
     <span>Project Management & Design Fees (on Article Value, ${Number(Number(pmFeePercentage || 0).toFixed(2))}%)</span>
      <span>₹ ${Math.round(projectManagementFee).toLocaleString("en-IN")}</span>
    </div>
    <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
  `
        : "";

    const showGst =
      applyGst && gstAmount > 0
        ? `
    <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
      <span>${gstMode === "inclusive" ? "Inclusive GST (already included)" : "Exclusive GST (extra)"} (${Number(Number(gstPercentage || 0).toFixed(2))}%)</span>
      <span>₹ ${Math.round(gstAmount).toLocaleString("en-IN")}</span>
    </div>
    <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
  `
        : "";

    const costDiv = document.createElement("div");
    costDiv.style.width = "210mm";
    costDiv.style.minHeight = "297mm";
    costDiv.style.padding = `${margin}mm`;
    costDiv.style.boxSizing = "border-box";
    costDiv.style.fontFamily = "Calibri, sans-serif";
    costDiv.style.backgroundColor = "#d3d3d3"; // ✅ fixed typo

    costDiv.innerHTML = `
  <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
  <div style="font-size: 14px; margin-bottom: 10px;">
    Client: ${clientName}<br/>Address: ${clientAddress}
    <span style="float:right;">Date: ${new Date().toLocaleDateString(
      "en-IN",
    )}</span>
  </div>

  <h2 style="
    text-align: center;
    font-size: 26px;
    font-weight: bold;
    color: #343A76;
    margin-top: 60px;
    margin-bottom: 30px;
  ">
    Total Cost Breakup<br/>
    (Site Service)
  </h2>

  <div style="
    background-color: #fff3b0;
    border-radius: 25px;
    padding: 25px 35px;
    width: 80%;
    margin: 0 auto;
    box-shadow: 5px 8px 12px rgba(0, 0, 0, 0.08);
  ">
    <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
      <span>Total Article Value</span>
      <span>₹ ${Math.round(totalMRP).toLocaleString("en-IN")}</span>
    </div>
    <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />

    ${showPmFee}
    ${showDiscount}
    ${showGst}

    <div style="display: flex; justify-content: space-between; font-size: 22px; font-weight: bold; color: #343A76;">
      <span>Total</span>
      <span>₹ ${Math.round(finalValue).toLocaleString("en-IN")}</span>
    </div>
    <hr style="border-top: 1.5px solid #6f4e37; margin: 10px 0 15px 0;" />

    <div style="font-size: 14px; color: #6f4e37; margin-top: 15px;">
      ${
        applyGst
          ? gstMode === "inclusive"
            ? "* Inclusive GST is already included in Total and is not added again.<br/>"
            : "* Exclusive GST has been added to Total.<br/>"
          : ""
      }
      * This quote will expire on 
      <strong>${new Date(
        Date.now() + 30 * 24 * 60 * 60 * 1000,
      ).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })}</strong>
    </div>

    <div style="text-align: right; margin-top: 30px; font-size: 20px; color: #343A76;">
  <strong>Prepared by:</strong> ${loginUserName}
  <br/>
  <span style="color: grey; font-size: 14px;">
    (If you have any questions about the quotation, feel free to reach out to your designer.)
  </span>
</div>

  </div>
`;

    // ROOM SUMMARY SECTION

    const createRoomSummaryDiv = () => {
      const div = document.createElement("div");
      div.style.width = "210mm";
      div.style.minHeight = "297mm";
      div.style.padding = "15mm";
      div.style.boxSizing = "border-box";
      div.style.fontFamily = "Calibri, sans-serif";
      div.style.backgroundColor = "#d3d3d3";

      div.innerHTML = `
        <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
        <div style="font-size: 14px; margin-bottom: 10px;">
          Client: ${clientName}<br/>Address: ${clientAddress}
          <span style="float:right;">Date: ${new Date().toLocaleDateString(
            "en-IN",
          )}</span>
        </div>
    
        <!-- Room Summary Title -->
        <div style="font-size: 26px; font-weight: bold; color: #343A76; margin-bottom: 8px; margin-top: 30px; text-align: center;">
          <span style="color: #343A76; padding-left: 10px;">ROOM SUMMARY</span>
        </div>
    
        <!-- Subheading -->
        <div style="font-size: 16px; font-style: italic; color: #343A76; margin-bottom: 30px; text-align: center;">
          The Sweetest Memories Start From Here. <br/>
          And here's what they cost
        </div>
    
        <!-- Line separator -->
        <div style="width: 100%; border-top: 2px solid #343A76; margin: 20px 0;"></div>
    
        <!-- Card with table -->
        <div style="
          background-color: #fff3b0;
          border-radius: 15px;
          box-shadow: 0px 12px 20px rgba(0, 0, 0, 0.2);
          padding: 25px;
          width: 90%;
          margin: auto;
        ">
          <table style="width: 100%; border-collapse: collapse; font-size: 14px; color: #5a4a42;">
            <thead>
              <tr style="border-bottom: 1px solid #6f4e37;">
                <th style="text-align: left; padding: 8px;">SL No</th>
                <th style="text-align: left; padding: 8px;">Room Name</th>
                <th style="text-align: right; padding: 8px;">Cost Per Room</th>
              </tr>
            </thead>
            <tbody>
              ${roomSummaryData
                .map(
                  (room, index) => `
                    <tr style="border-bottom: 1px solid #d3d3d3;">
                      <td style="padding: 8px;">${index + 1}</td>
                      <td style="padding: 8px;">${room.roomName}</td>
                      <td style="padding: 8px; text-align: right;">₹ ${room.cost.toLocaleString(
                        "en-IN",
                      )}</td>
                    </tr>
                  `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
      `;

      return div;
    };

    // ✅ NOTES SECTION SPLIT
    const createStyledNotesDiv = () => {
      const div = document.createElement("div");
      div.style.width = "210mm";
      div.style.minHeight = "297mm";
      div.style.padding = "15mm";
      div.style.boxSizing = "border-box";
      div.style.fontFamily = "Calibri, sans-serif";
      div.style.fontSize = "14px";
      div.style.lineHeight = "1.5";
      div.style.backgroundColor = "#d3d3d3";

      div.innerHTML = `
        <h2 style="font-size: 20px; margin-bottom: 10px;">Notes</h2>
    <div style="background-color: #fff3b0; border: 1px solid #000; padding: 15px; border-radius: 15px;">
      <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
            <li>
              <strong>Payment Stages:</strong><br/>
              1. Stage 1: 10% Booking amount.<br/>
              2. Stage 2: 50% Before Starting of project.<br/>
              3. Stage 3: 40% Before finishing stage.
            </li><br/>
    
            <li>
              <strong>Material:</strong><br/>
              1. Plywood - ISI grade 303 - Red and White.<br/>
              2. Decorative Laminate will be used of maximum of 1500 Rs/Sheet.<br/>
              3. Light fitting as per standard make with 2 years of warranty<br/>
              4. Paint - Asian paint Royal or similar. <br/>
              5. Wire-RR or Finolex as per standard ISI graded.
            </li><br/>
    
            <li>
              <strong>General Condition:</strong><br/>
              1. Any payment made is non-refundable.<br/>
              2. Initial quote is based on primary discussion, after design stage & site visit quote may vary as per design revisions & site conditions.<br/>
              3. Item brands may vary as per product availability.<br/>
              4. Electricity required for site work should be provided by owner.<br/>
              5. Quotation is valid for 1 month only.<br/>
              6. Society approvals, municipal approvals, structural approvals, and Fire approvals are in client scope only.<br/>
              7. Softvolute Buildcon LLP not responsible for any legal approvals required for this work.<br/>
              8. If work get hold during execution for any reason, quotation is subject to revise.<br/>
              9. Material storage space needs to be provided by owners. Material storage space should be within working premises, so it ensures safety.<br/>
              10. Appliances are in client scope.
            </li><br/>
    
            <li>
              <strong>Warranty:</strong><br/>
              1. The Product warranty is - As per manufacturer warranty<br/>
              2. Bed hydraulic mechanism with 2 years of warranty as per manufacturer policy<br/>
              3. General repair warranty is for 2 years, but if found broken or damaged purposely, an extra charge will be imposed.<br/>
              4. After completion, the warranty period starts. For examination visits, a charge of Rs.500 + GST must be paid in advance.
            </li><br/>
    
            <li>
              <strong>Payment Charges:</strong><br/>
           1. Payment from <strong>credit card – 2.9%</strong> bank charges shall be extra, payment <strong>via link – 2.5%</strong> bank charges extra, and payment from <strong>debit card – 1.9%</strong> bank charges.

.<br/>
              2. Payment via QR code, UPI, NEFT and RTGS will not attract any extra charges.
            </li>
          </ul>
        </div>
   `;
      return div;
    };

    const createNotesFooterDiv = () => {
      const div = document.createElement("div");
      div.style.width = "210mm";
      div.style.minHeight = "297mm";
      div.style.padding = "15mm";
      div.style.boxSizing = "border-box";
      div.style.fontFamily = "Calibri, sans-serif";
      div.style.fontSize = "14px";
      div.style.lineHeight = "1.5";
      div.style.backgroundColor = "#d3d3d3";

      div.innerHTML = `
    <div style="margin-top: 40px; font-size: 14px;">
      <div style="display: flex; justify-content: space-between;">
        <div>
          <strong>Bank Details</strong><br/>
          Account Name: SOFTVOLUTE BUILDCON LLP <br/>
          Bank: DBS BANK<br/>
          A/C No: 8831210000001915<br/>
          IFSC: DBSS0IN0831
        </div>

        <div style="display: flex; gap: 20px; align-items: center;">
          <img src="/QRCOde.jpg" style="width: 120px;" alt="QR Code" />
          <img src="/IndQr.jpg" style="width: 120px;" alt="QR Code" />
        </div>
      </div>

      <div style="margin-top: 20px;">
        <div style="border-bottom: 1px solid black; width: 200px; margin: 8px 0;"></div>
        <strong>Customer Sign & Date</strong><br/> 
        <span style="font-size: 12px;">(Email confirmation also accepted)</span>
      </div>

      <div style="text-align: right; margin-top: 30px; font-size: 20px; color: #343A76;">
        <strong>Prepared by:</strong> ${loginUserName}
      </div>
    </div>
  `;

      return div;
    };

    // ✅ Export all parts
    // Cover page uses the first jsPDF page.
    document.body.appendChild(coverDiv);
    await renderToPDFPages(coverDiv);
    document.body.removeChild(coverDiv);

    // Every following section starts on a fresh PDF page.
    for (let i = 0; i < quoteDivs.length; i++) {
      pdf.addPage();
      const div = quoteDivs[i];
      document.body.appendChild(div);
      await renderToPDFPages(div);
      document.body.removeChild(div);
    }

    pdf.addPage();
    document.body.appendChild(costDiv);
    await renderToPDFPages(costDiv);
    document.body.removeChild(costDiv);

    pdf.addPage();
    const roomSummaryDiv = createRoomSummaryDiv();
    document.body.appendChild(roomSummaryDiv);
    await renderToPDFPages(roomSummaryDiv);
    document.body.removeChild(roomSummaryDiv);

    // Notes Content Page
    pdf.addPage();
    const notesDivFinal = createStyledNotesDiv();
    document.body.appendChild(notesDivFinal);
    await renderToPDFPages(notesDivFinal);
    document.body.removeChild(notesDivFinal);

    // Notes Footer Page
    pdf.addPage();
    const notesFooterDiv = createNotesFooterDiv();
    document.body.appendChild(notesFooterDiv);
    await renderToPDFPages(notesFooterDiv);
    document.body.removeChild(notesFooterDiv);

    // ✅ Save file
    pdf.save("quote.pdf");
  };

  // PDF END
  // Export Excel

  const exportQuoteTableToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Quote");

    // 1. Company Header
    worksheet.mergeCells("A1:E1");
    worksheet.getCell("A1").value = "SoftVolute Buildcon LLP";
    worksheet.getCell("A1").font = {
      bold: true,
      size: 18,
      color: { argb: "FF343A76" },
    };
    worksheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    worksheet.getCell("A2").value = `Client: ${clientName || ""}`;
    worksheet.getCell("A3").value = `Address: ${clientAddress || ""}`;
    worksheet.getCell("E2").value = `Date: ${new Date().toLocaleDateString(
      "en-IN",
    )}`;
    worksheet.getCell("E2").alignment = { horizontal: "right" };

    // 2. Section Title
    worksheet.mergeCells("A5:E5");
    worksheet.getCell("A5").value = "Quote Details";
    worksheet.getCell("A5").font = { bold: true, size: 16 };
    worksheet.getCell("A5").alignment = { horizontal: "center" };

    // 3. Table Headers
    const headerRow = worksheet.addRow([
      "Sr No",
      "Image",
      "Item Name",
      "Room",
      "Price (INR)",
    ]);

    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF707171" },
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // 4. Table Data
    items.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        "",
        (item.label || "") + (item.description ? `\n${item.description}` : ""),
        item.room || "",
        item.price || item.MRP || 0,
      ]);
      row.height = 60;

      // 🛠️ Wrap text inside Item Name cell (Column 3)
      row.getCell(3).alignment = {
        vertical: "top",
        horizontal: "left",
        wrapText: true,
      };

      // 🛠️ Center align Sr No, Image, Room, Price nicely
      row.getCell(1).alignment = { vertical: "middle", horizontal: "center" }; // Sr No
      row.getCell(2).alignment = { vertical: "middle", horizontal: "center" }; // Image
      row.getCell(4).alignment = { vertical: "middle", horizontal: "center" }; // Room
      row.getCell(5).alignment = { vertical: "middle", horizontal: "right" }; // Price
    });

    // 5. Set Columns Widths
    worksheet.columns = [
      { width: 8 }, // Sr No
      { width: 20 }, // Image
      { width: 60 }, // Item Name
      { width: 20 }, // Room
      { width: 15 }, // Price
    ];

    // Gap after table
    worksheet.addRow([]);

    // 5. Cost Breakup Section
    worksheet.addRow([]); // One gap before heading

    // 📍 Add only 1 heading
    worksheet.mergeCells(
      `A${worksheet.lastRow.number + 1}:E${worksheet.lastRow.number + 1}`,
    );
    const totalBreakupHeading = worksheet.getRow(worksheet.lastRow.number);
    totalBreakupHeading.getCell(1).value = "Total Cost Breakup (Site Service)";
    totalBreakupHeading.font = {
      bold: true,
      size: 16,
      color: { argb: "FF343A76" },
    };
    totalBreakupHeading.alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    worksheet.addRow([]); // Gap row after heading

    const totals = calculateTotal();
    const priceSummary = [
      {
        label: "Total Article Value",
        value: totals.totalMRP || 0,
        type: "article",
      },
      ...(applyPmFee
        ? [
            {
              label: `Project Management Fee (on Article Value, ${Number(Number(pmFeePercentage || 0).toFixed(2))}%)`,
              value: totals.projectManagementFee || 0,
              type: "pm",
            },
          ]
        : []),
      ...(applyDiscount
        ? [
            {
              label: `Total Discount (on Article Value, ${Number(discountPercentage || 0)}%)`,
              value: -(totals.discountAmount || 0),
              type: "discount",
            },
          ]
        : []),
      ...(applyGst
        ? [
            {
              label: `${gstMode === "inclusive" ? "Inclusive GST (included, not added)" : "Exclusive GST (extra)"} (${Number(Number(gstPercentage || 0).toFixed(2))}%)`,
              value: totals.gstAmount || 0,
              type: "gst",
            },
          ]
        : []),
      { label: "Total", value: totals.finalValue || 0, type: "total" },
    ];

    // 🛠️ Summary rows
    priceSummary.forEach(({ label, value, type }) => {
      const row = worksheet.addRow([label, "", "", "", value]);

      worksheet.mergeCells(`A${row.number}:D${row.number}`);

      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
      row.getCell(5).alignment = { horizontal: "right", vertical: "middle" };

      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFACD" },
        };
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
      });

      row.getCell(1).font = { bold: true };
      row.getCell(5).font = { bold: true };

      if (type === "discount") {
        row.getCell(1).font = { bold: true, color: { argb: "FF008000" } };
        row.getCell(5).font = { bold: true, color: { argb: "FF008000" } };
      }

      if (type === "total") {
        row.getCell(1).font = { bold: true, color: { argb: "FF343A76" } };
        row.getCell(5).font = { bold: true, color: { argb: "FF343A76" } };
      }
    });

    // 🛠️ ✅ Add extra gap after Summary before Notes
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);

    // 7. Notes Section start properly after gap
    const notesData = [
      "* Payment Stages:",
      "1. Stage 1: 10% Booking amount.",
      "2. Stage 2: 50% Before Starting of project.",
      "3. Stage 3: 40% Before finishing stage.",
      "",
      "* Material:",
      "1. Plywood - ISI grade 303 - Red and White.",
      "2. Decorative Laminate 1500 Rs/Sheet",
      "3. Light fitting ISI mark (2 yrs warranty)",
      "4. Paint - Asian paint royal/similar.",
      "5. Wire - RR or Finolex ISI grade",
      "",
      "* General Conditions:",
      "1. Any payment is non-refundable.",
      "2. Design stage and site visit may change quote.",
      "3. Item brands may vary.",
      "4. Electricity to be provided by owner.",
      "5. Quotation valid for 1 month.",
      "",
      "* Warranty:",
      "1. Product warranty as per manufacturer.",
      "2. Hydraulic mechanism 2 years warranty.",
      "3. General repair warranty 2 years.",
      "",
      "* Payment Charges:",
      "1. Credit card 2.9%, Link 2.5%, Debit card 1.9%",
      "2. QR/UPI/NEFT/RTGS no extra charges.",
    ];
    // 🛠️ Correct Notes
    notesData.forEach((text) => {
      const row = worksheet.addRow([text]);
      worksheet.mergeCells(`A${row.number}:E${row.number}`);
      row.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFACD" }, // Same yellow background
      };
      row.getCell(1).alignment = {
        vertical: "top",
        horizontal: "left",
        wrapText: true,
      };
      row.height = 20;
    });
    // 🛠️ Gap after Notes
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);

    // 📍 Bank Details Section
    const bankDetails = [
      "Bank Details",
      "Account Name: SOFTVOLUTE BUILDCON LLP",
      "Bank: DBS BANK",
      "A/C No: 883112000001915",
      "IFSC: DBSS0IN0831",
    ];

    // 🛠️ Add Bank Details
    bankDetails.forEach((text, idx) => {
      const row = worksheet.addRow([text]);
      worksheet.mergeCells(`A${row.number}:C${row.number}`); // Left side Bank Details
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
      row.getCell(1).font = {
        bold: idx === 0 ? true : false,
        size: idx === 0 ? 12 : 11,
      }; // Only "Bank Details" title bold
    });

    // 📍 Add Gap for QR placement
    worksheet.addRow([]);
    worksheet.addRow([]);

    // 📍 Customer Sign and Date Section
    const signRow = worksheet.addRow(["Customer Sign & Date"]);
    worksheet.mergeCells(`A${signRow.number}:C${signRow.number}`);
    signRow.getCell(1).font = { bold: true };
    signRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    // 📍 Email Confirmation Line
    const emailRow = worksheet.addRow(["(Email confirmation also accepted)"]);
    worksheet.mergeCells(`A${emailRow.number}:C${emailRow.number}`);
    emailRow.getCell(1).font = { italic: true, size: 10 };
    emailRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    // 8. Save Excel
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, "QuoteTable.xlsx");
  };

  // Export Excel END

  const {
    totalMRP,
    projectManagementFee,
    discountAmount,
    gstAmount,
    finalValue,
  } = calculateTotal();

  const handleDelete = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  return (
    <div className="quotes-page">
      <section className="quotes-card quotes-main-card">
        <div className="quotes-header">
          <button
            onClick={() => navigate("/project")}
            className="quote-btn quote-btn-back"
          >
            ← Back
          </button>

          <h2 className="quote-page-title">Create Quote</h2>
        </div>

        <div className="quote-selector-grid">
          <div className="quote-field quote-field-wide">
            <label>Select Quote / Master Item</label>
            <Select
              options={filteredQuoteOptions}
              value={selectedItem}
              onChange={setSelectedItem}
              isClearable
              onInputChange={(value) => {
                setItemSearchText(value);
                return value;
              }}
              placeholder="Search item, description, category, MRP..."
              isSearchable
              noOptionsMessage={() => "No item found"}
              classNamePrefix="quote-select"
              formatOptionLabel={(option) => (
                <div
                  dangerouslySetInnerHTML={{
                    __html: highlightMatch(option.label, itemSearchText),
                  }}
                />
              )}
            />
          </div>

          <div className="quote-field">
            <label>Room</label>
            <Select
              options={roomOptions}
              value={selectedRoom}
              onChange={setSelectedRoom}
              placeholder="Select Room..."
              isSearchable
              classNamePrefix="quote-select"
            />
          </div>
          <div className="quote-field">
            <label>Unit</label>
            <select
              className="quote-unit-input"
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
            >
              <option value="QNT">QNT</option>
              <option value="NOS">NOS</option>
              <option value="SQ.FT">SQ.FT</option>
            </select>
          </div>

          <div className="quote-field">
            <label>Qty / Area</label>
            <input
              className="quote-unit-input"
              type="number"
              min="0.01"
              step="any"
              value={selectedQty}
              onChange={(e) => setSelectedQty(e.target.value)}
            />
          </div>
          <div className="quote-top-actions">
            {canAddQuoteItem && (
              <button className="quote-btn quote-btn-primary" onClick={addItem}>
                + Add Quote Item
              </button>
            )}

            {canAddMasterItem && !showCustomRow && (
              <button
                className="quote-btn quote-btn-outline"
                type="button"
                onClick={() => {
                  resetCustomItem();
                  setShowCustomRow(true);
                }}
              >
                + Add Custom Item
              </button>
            )}

            {canCreateCustomerQuote && (
              <button
                type="button"
                className="quote-btn quote-btn-purple"
                onClick={() =>
                  navigate("/customer-quote", {
                    state: {
                      projectName: project?.name || "",
                      clientName,
                      clientAddress,
                    },
                  })
                }
              >
                Create Customer Quote
              </button>
            )}
          </div>
        </div>

        {isEditing && editIndex !== null && (
          <div className="quote-edit-banner">
            <span>Editing Quote-{editIndex + 1}</span>

            <button
              type="button"
              className="quote-edit-close-btn"
              onClick={handleCancelEdit}
              title="Cancel Editing"
            >
              ×
            </button>
          </div>
        )}
        {(items.length > 0 || showCustomRow) && (
          <div id="quote-container" className="quote-table-section">
            <div className="quote-section-title">Quote Items</div>
            <div className="quote-table-wrap">
              <table className="quote-items-table">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Image</th>
                    <th>Item Name</th>
                    <th>Description</th>
                    <th>Room</th>
                    <th>Unit</th>
                    <th>Qty</th>
                    <th>Length</th>
                    <th>Width</th>
                    <th>Area</th>
                    {canShowPrice && <th>Price (INR)</th>}
                    {canShowPrice && applyDiscount && <th>Discounted Price</th>}
                    {canAccess("quote", "delete") && <th>Action</th>}
                  </tr>
                </thead>

                <tbody>
                  {items.length === 0 && !showCustomRow && (
                    <tr>
                      <td
                        className="quote-empty-row"
                        colSpan={
                          10 +
                          (canShowPrice ? 1 : 0) +
                          (canShowPrice && applyDiscount ? 1 : 0) +
                          (canAccess("quote", "delete") ? 1 : 0)
                        }
                      >
                        No quote items added yet.
                      </td>
                    </tr>
                  )}

                  {items.map((item, index) => (
                    <tr key={item.itemId || `${item.label}-${index}`}>
                      <td>{index + 1}</td>
                      <td>
                        {item.image ? (
                          <img
                            className="quote-item-image"
                            src={
                              item.image.startsWith("data:image")
                                ? item.image
                                : item.image.startsWith("http")
                                  ? item.image
                                  : item.image.startsWith("/uploads")
                                    ? `${BASE_URL}${item.image}`
                                    : item.image.startsWith("/Images")
                                      ? item.image
                                      : `/${item.image}`
                            }
                            alt={item.label || "item"}
                          />
                        ) : (
                          <div className="quote-image-placeholder">
                            No image
                          </div>
                        )}
                      </td>
                      <td className="quote-item-name">{item.label}</td>
                      <td className="quote-description-cell">
                        {item.description || "-"}
                      </td>
                      <td>{item.room || "-"}</td>
                      <td className="quote-unit-cell">{item.unit || "NOS"}</td>
                      <td className="quote-qty-cell">{item.quantity ?? 1}</td>
                      <td>{Number(item.length || 0)}</td>
                      <td>{Number(item.width || 0)}</td>
                      <td>
                        {Number(
                          item.area ||
                            Number(item.length || 0) * Number(item.width || 0),
                        ).toLocaleString("en-IN")}
                      </td>
                      {canShowPrice && (
                        <td className="quote-money-cell">
                          ₹{" "}
                          {Number(item.MRP || item.price || 0).toLocaleString(
                            "en-IN",
                          )}
                        </td>
                      )}
                      {canShowPrice && applyDiscount && (
                        <td className="quote-money-cell quote-discounted-cell">
                          ₹{" "}
                          {Math.round(
                            Number(item.MRP || item.price || 0) -
                              (Number(item.MRP || item.price || 0) *
                                Number(discountPercentage || 0)) /
                                100,
                          ).toLocaleString("en-IN")}
                        </td>
                      )}
                      {canAccess("quote", "delete") && (
                        <td>
                          <button
                            className="quote-delete-btn"
                            onClick={() => handleDelete(index)}
                            type="button"
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}

                  {showCustomRow && (
                    <tr className="quote-custom-row">
                      <td>{items.length + 1}</td>
                      <td>
                        <label className="quote-upload-btn">
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                              handleCustomImage(e.target.files?.[0])
                            }
                          />
                        </label>
                        {customItem.image && (
                          <img
                            className="quote-custom-preview"
                            src={customItem.image}
                            alt="custom preview"
                          />
                        )}
                      </td>
                      <td>
                        <input
                          className="quote-table-input"
                          value={customItem.label}
                          onChange={(e) =>
                            updateCustomField("label", e.target.value)
                          }
                          placeholder="Item name"
                        />
                      </td>
                      <td>
                        <input
                          className="quote-table-input"
                          value={customItem.description}
                          onChange={(e) =>
                            updateCustomField("description", e.target.value)
                          }
                          placeholder="Description"
                        />
                      </td>
                      <td>
                        <select
                          className="quote-table-input"
                          value={customItem.room}
                          onChange={(e) =>
                            updateCustomField("room", e.target.value)
                          }
                        >
                          <option value="">Select Room</option>
                          {roomOptions.map((room) => (
                            <option key={room.value} value={room.value}>
                              {room.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="quote-table-input quote-unit-select"
                          value={customItem.unit}
                          onChange={(e) =>
                            updateCustomField("unit", e.target.value)
                          }
                        >
                          <option value="QNT">QNT</option>
                          <option value="NOS">NOS</option>
                          <option value="SQ.FT">SQ.FT</option>
                        </select>
                      </td>
                      <td>
                        <input
                          className="quote-table-input quote-qty-input"
                          type="number"
                          min="0.01"
                          step="any"
                          value={customItem.quantity}
                          onChange={(e) =>
                            updateCustomField("quantity", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="quote-table-input quote-number-input"
                          type="number"
                          min="0"
                          value={customItem.length}
                          onChange={(e) =>
                            updateCustomField("length", e.target.value)
                          }
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <input
                          className="quote-table-input quote-number-input"
                          type="number"
                          min="0"
                          value={customItem.width}
                          onChange={(e) =>
                            updateCustomField("width", e.target.value)
                          }
                          placeholder="0"
                        />
                      </td>
                      <td className="quote-custom-area">
                        {(
                          Number(customItem.length || 0) *
                          Number(customItem.width || 0)
                        ).toLocaleString("en-IN")}
                      </td>
                      {canShowPrice && (
                        <td>
                          <input
                            className="quote-table-input quote-price-input"
                            type="number"
                            min="0"
                            value={customItem.MRP}
                            onChange={(e) =>
                              updateCustomField("MRP", e.target.value)
                            }
                            placeholder="0"
                          />
                        </td>
                      )}
                      {canShowPrice && applyDiscount && (
                        <td className="quote-money-cell quote-discounted-cell">
                          ₹{" "}
                          {Math.round(
                            Number(customItem.MRP || 0) -
                              (Number(customItem.MRP || 0) *
                                Number(discountPercentage || 0)) /
                                100,
                          ).toLocaleString("en-IN")}
                        </td>
                      )}
                      {canAccess("quote", "delete") && (
                        <td>
                          <div className="quote-inline-actions">
                            <button
                              className="quote-add-inline-btn"
                              type="button"
                              onClick={addCustomItemInline}
                            >
                              Add
                            </button>
                            <button
                              className="quote-cancel-inline-btn"
                              type="button"
                              onClick={() => {
                                resetCustomItem();
                                setShowCustomRow(false);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {canShowFinalAmount && canShowPrice && (
          <section className="quote-summary-section">
            <div className="quote-section-title">Quote Summary & Charges</div>
            <div className="quote-summary-grid">
              <div className="quote-summary-card quote-summary-article">
                <span className="quote-summary-label">Article Value</span>
                <strong>
                  ₹ {Math.round(totalMRP).toLocaleString("en-IN")}
                </strong>
              </div>

              <div
                className={`quote-summary-card quote-summary-discount ${
                  applyDiscount ? "is-active" : ""
                }`}
              >
                <label className="quote-charge-toggle">
                  <input
                    type="checkbox"
                    checked={applyDiscount}
                    onChange={(e) => setApplyDiscount(e.target.checked)}
                  />
                  <span>Apply Discount</span>
                </label>
                <div className="quote-percent-input">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={!applyDiscount}
                    value={discountPercentage}
                    onChange={(e) => {
                      const value = Math.max(
                        0,
                        Math.min(100, Number(e.target.value || 0)),
                      );
                      setDiscountPercentage(value);
                    }}
                  />
                  <span>%</span>
                </div>
                <div className="quote-charge-amount">
                  <span>Discount Amount (on Article Value)</span>
                  <strong>
                    - ₹ {Math.round(discountAmount).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>

              <div
                className={`quote-summary-card quote-summary-pm ${
                  applyPmFee ? "is-active" : ""
                }`}
              >
                <label className="quote-charge-toggle">
                  <input
                    type="checkbox"
                    checked={applyPmFee}
                    onChange={(e) => setApplyPmFee(e.target.checked)}
                  />
                  <span>Add Project Management Fee</span>
                </label>
                <div className="quote-percent-input">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={!applyPmFee}
                    value={pmFeePercentage}
                    onChange={(e) => {
                      const value = Math.max(
                        0,
                        Math.min(100, Number(e.target.value || 0)),
                      );
                      setPmFeePercentage(Number(value.toFixed(2)));
                    }}
                  />
                  <span>%</span>
                </div>
                <div className="quote-charge-amount">
                  <span>PM Fee Amount (on Article Value)</span>
                  <strong>
                    ₹ {Math.round(projectManagementFee).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>

              <div
                className={`quote-summary-card quote-summary-gst ${
                  applyGst ? "is-active" : ""
                }`}
              >
                <label className="quote-charge-toggle">
                  <input
                    type="checkbox"
                    checked={applyGst}
                    onChange={(e) => setApplyGst(e.target.checked)}
                  />
                  <span>Add GST</span>
                </label>
                <select
                  value={gstMode}
                  disabled={!applyGst}
                  onChange={(e) => setGstMode(e.target.value)}
                  aria-label="GST calculation type"
                  style={{
                    width: "100%",
                    minHeight: 32,
                    padding: "5px 8px",
                    border: "1px solid #d7dee8",
                    borderRadius: 7,
                    background: applyGst ? "#fff" : "#f5f7f9",
                    color: "#334155",
                    margin: "6px 0",
                    fontSize: 12,
                  }}
                >
                  <option value="exclusive">Exclusive GST</option>
                  <option value="inclusive">Inclusive GST</option>
                </select>
                <div className="quote-percent-input">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={!applyGst}
                    value={gstPercentage}
                    onChange={(e) =>
                      setGstPercentage(
                        Math.max(0, Math.min(100, Number(e.target.value || 0))),
                      )
                    }
                  />
                  <span>%</span>
                </div>
                <div className="quote-charge-amount">
                  <span>
                    {gstMode === "inclusive"
                      ? "Included GST Amount"
                      : "GST Amount"}
                  </span>
                  <strong>
                    ₹ {Math.round(gstAmount).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>

              <div className="quote-summary-card quote-summary-final">
                <span className="quote-summary-label">Final Value</span>
                <strong>
                  ₹ {Math.round(finalValue).toLocaleString("en-IN")}
                </strong>
                {applyGst && gstMode === "inclusive" && (
                  <small>GST included — not added again</small>
                )}
              </div>

              <div className="quote-document-row">
                <span className="quote-document-title">Documents</span>
                <label
                  className={`quote-document-upload ${
                    documentUploading ? "is-disabled" : ""
                  }`}
                >
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    disabled={documentUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleQuoteDocumentUpload(file);
                      e.target.value = "";
                    }}
                  />
                  {documentUploading ? "Uploading..." : "+ Upload Document"}
                </label>

                <div className="quote-document-list">
                  {quoteDocuments.map((doc, index) => (
                    <div
                      className="quote-document-chip"
                      key={`${doc.url}-${index}`}
                    >
                      <button
                        type="button"
                        className="quote-document-open"
                        title={doc.name}
                        onClick={() =>
                          window.open(
                            getQuoteFileUrl(doc.url),
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        {doc.name}
                      </button>
                      <button
                        type="button"
                        className="quote-document-remove"
                        onClick={() => removeQuoteDocument(index)}
                        title="Remove document"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="quote-formula-note">
              <strong>Formula:</strong> Article Value + PM Fee − Discount
              {applyGst && gstMode === "exclusive" ? " + Exclusive GST" : ""}
              {" = Final Value."}
              {" PM Fee is calculated on Article Value. "}
              {
                "Discount is calculated on Article Value only (PM Fee excluded)."
              }
              {applyGst && gstMode === "inclusive"
                ? " Inclusive GST is already included in the discounted amount and is not added again."
                : ""}
            </div>
          </section>
        )}

        <div className="quote-main-actions">
          <button className="quote-btn quote-btn-primary" onClick={saveQuote}>
            {isEditing ? "Save as New Version" : "Save Quote"}
          </button>

          {canExportPdf && (
            <button className="quote-btn quote-btn-pink" onClick={exportToPDF}>
              Export to PDF
            </button>
          )}

          {canExportExcel && (
            <button
              className="quote-btn quote-btn-green"
              onClick={exportQuoteTableToExcel}
            >
              Export to Excel
            </button>
          )}
        </div>
      </section>

      <section className="quotes-card saved-quotes-card">
        <div className="quote-section-title">Saved Quotes</div>
        <div className="quote-table-wrap">
          <table className="quote-saved-table">
            <thead>
              <tr>
                <th>Quote No</th>
                <th>Client</th>
                {canShowPrice && <th>Article Value</th>}
                {canShowPrice && <th>Discount</th>}
                {canShowPrice && <th>PM Fee</th>}
                {canShowPrice && <th>GST</th>}
                {canShowFinalAmount && <th>Final Amount</th>}
                <th>Documents</th>
                <th>Date</th>
                <th>Status</th>
                {canEditSavedQuote && <th>Actions</th>}
                {canSendMail && <th>Send Mail</th>}
              </tr>
            </thead>
            <tbody>
              {savedQuotes.length === 0 ? (
                <tr>
                  <td
                    className="quote-empty-row"
                    colSpan={
                      5 +
                      (canShowPrice ? 4 : 0) +
                      (canShowFinalAmount ? 1 : 0) +
                      (canEditSavedQuote ? 1 : 0) +
                      (canSendMail ? 1 : 0)
                    }
                  >
                    No saved quotes found.
                  </td>
                </tr>
              ) : (
                savedQuotes.map((quote, index) => {
                  const quoteTotal = Number(quote.totalMRP || 0);
                  const quoteDiscount = Number(quote.discountAmount || 0);
                  const quoteAfterDiscount = quoteTotal - quoteDiscount;
                  const quotePmAmount = Number(quote.projectManagementFee || 0);
                  const quotePmPct = Number(
                    quote.projectManagementFeePercentage ??
                      (quoteTotal > 0 ? (quotePmAmount / quoteTotal) * 100 : 0),
                  );
                  const quoteDiscountPct = Number(
                    quote.discountPercentage ??
                      (quoteTotal > 0 ? (quoteDiscount / quoteTotal) * 100 : 0),
                  );
                  const quoteGstAmount = Number(quote.gstAmount || 0);
                  const quoteGstMode =
                    quote.gstMode === "inclusive" || quote.inclusiveGST === true
                      ? "inclusive"
                      : "exclusive";
                  const quoteGstBase =
                    quoteGstMode === "inclusive"
                      ? Number(quote.finalAmount || 0) - quoteGstAmount
                      : quoteAfterDiscount + quotePmAmount;
                  const quoteGstPct = Number(
                    quote.gstPercentage ??
                      (quoteGstBase > 0
                        ? (quoteGstAmount / quoteGstBase) * 100
                        : 0),
                  );

                  return (
                    <tr key={quote._id || index}>
                      <td>{index + 1}</td>
                      <td>{quote.clientName}</td>
                      {canShowPrice && (
                        <td className="quote-money-cell">
                          ₹ {quoteTotal.toLocaleString("en-IN")}
                        </td>
                      )}
                      {canShowPrice && (
                        <td>
                          {quoteDiscountPct > 0 ? (
                            <>
                              {quoteDiscountPct.toFixed(2)}%<br />
                              <span className="quote-table-subvalue">
                                - ₹ {quoteDiscount.toLocaleString("en-IN")}
                              </span>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      )}
                      {canShowPrice && (
                        <td>
                          {quotePmAmount > 0 ? (
                            <>
                              {quotePmPct.toFixed(2)}%<br />
                              <span className="quote-table-subvalue">
                                ₹ {quotePmAmount.toLocaleString("en-IN")}
                              </span>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      )}
                      {canShowPrice && (
                        <td>
                          {quoteGstAmount > 0 ? (
                            <>
                              {quoteGstPct.toFixed(2)}% (
                              {quoteGstMode === "inclusive"
                                ? "Inclusive"
                                : "Exclusive"}
                              )<br />
                              <span className="quote-table-subvalue">
                                ₹ {quoteGstAmount.toLocaleString("en-IN")}
                              </span>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      )}
                      {canShowFinalAmount && (
                        <td className="quote-money-cell quote-final-cell">
                          ₹{" "}
                          {Number(quote.finalAmount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </td>
                      )}
                      <td>
                        {(() => {
                          const docs = Array.isArray(quote.documents)
                            ? quote.documents
                            : Array.isArray(quote.clientDetails?.quoteDocuments)
                              ? quote.clientDetails.quoteDocuments
                              : [];

                          return docs.length ? (
                            <div className="quote-saved-docs">
                              {docs.map((doc, docIndex) => (
                                <button
                                  key={`${doc.url}-${docIndex}`}
                                  type="button"
                                  className="quote-saved-doc-btn"
                                  title={doc.name}
                                  onClick={() =>
                                    window.open(
                                      getQuoteFileUrl(doc.url),
                                      "_blank",
                                      "noopener,noreferrer",
                                    )
                                  }
                                >
                                  {doc.name || `Doc ${docIndex + 1}`}
                                </button>
                              ))}
                            </div>
                          ) : (
                            "-"
                          );
                        })()}
                      </td>
                      <td>
                        {quote.date
                          ? new Date(quote.date).toLocaleDateString("en-GB")
                          : "--"}
                      </td>
                      <td>
                        {canApproveRejectQuote ? (
                          <button
                            onClick={() => handleStatusChange(index)}
                            className={`quote-status-btn ${
                              quote.status === "Approved"
                                ? "is-approved"
                                : "is-rejected"
                            }`}
                          >
                            {quote.status}
                          </button>
                        ) : (
                          <span>{quote.status}</span>
                        )}
                      </td>
                      {canEditSavedQuote && (
                        <td>
                          {isEditing && editIndex === index ? (
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="quote-edit-btn quote-cancel-edit-btn"
                              title="Cancel Edit"
                            >
                              ×
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleEditQuote(index)}
                              className="quote-edit-btn"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                      {canSendMail && (
                        <td>
                          <EmailHelper
                            items={quote.items}
                            clientName={quote.clientName}
                            clientDetails={clientDetails}
                            discountPercentage={quoteDiscountPct}
                            projectManagementFeePercentage={quotePmPct}
                            gstPercentage={quoteGstPct}
                            gstMode={quoteGstMode}
                            gstAmount={quoteGstAmount}
                            calculateTotal={() => ({
                              totalMRP: quoteTotal,
                              discountAmount: quoteDiscount,
                              projectManagementFee: quotePmAmount,
                              gstAmount: quoteGstAmount,
                              finalValue: quote.finalAmount,
                            })}
                            isUpdatedQuote={true}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default Quotes;
